import React, { useCallback } from 'react';
import { TFunction } from 'i18next';

import { HyperlinkTarget } from '../../../components/atoms/Hyperlink';
import { ContentLink } from '../../../_lib/components/ContentLink';
import { asDraggableContentListItem, useSortableListItem } from '../../../_lib/components/SortableContentList';
import { ExternalLinksEditableElement } from '../ExternalLinksTypes';
import { StatusInfoError } from '../../../_lib/components/StatusInfo';
import { ExtrenalLinksForm } from './ExternalLinksAddOrUpdateForm';
import { getItemHelpText, getUrlProtocol } from '../../../components/atoms/Hyperlink/Hyperlink.utils';
import {
  ContainerWithCommonContextMenu,
} from '../../../_lib/components/ContextMenu';
import { TooltipPlacement } from '../../../components/atoms/Tooltip';
import { useSonTranslation } from '../../../translation/useSonTranslation';
import { ContextMenuButtonProps } from '../../../_lib/components/ContextMenu/partials/ContextMenuButton';

type ExternalLinksListItemProps = ExternalLinksEditableElement & {
  openEditForm?: (form: ExtrenalLinksForm) => void;
  openDeleteForm?: (form: ExtrenalLinksForm) => void;
};

function ExternalLinkListItem({ id, link, delete: deleteForm,
  edit: editForm, openDeleteForm, openEditForm }: ExternalLinksListItemProps) {
  const { t } = useSonTranslation('ExternalLinks');

  const { move, nextItemId, prevItemId, error } = useSortableListItem(id);

  const moveElementUp = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      if (prevItemId) {
        event.preventDefault();
        event.stopPropagation();

        move(prevItemId);
      }
    }, [move, prevItemId]);

  const moveElementDown = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    if (nextItemId) {
      event.preventDefault();
      event.stopPropagation();

      move(nextItemId);
    }
  }, [move, nextItemId]);

  const contextMenuActions: ContextMenuButtonProps[] = [
    ...openEditForm
      ? [{
          children: t('editLink'),
          key: t('editLink'),
          name: 'editForm',
          'aria-haspopup': true,
          onClick: (event) => {
            event.preventDefault();
            event.stopPropagation();

            openEditForm(editForm);
          },
        }] as ContextMenuButtonProps[]
      : [],
    ...openDeleteForm
      ? [{
          children: t('removeLink'),
          key: t('removeLink'),
          name: 'removeForm',
          'aria-haspopup': true,
          onClick: (event) => {
            event.preventDefault();
            event.stopPropagation();

            openDeleteForm(deleteForm);
          },
        }] as ContextMenuButtonProps[]
      : [],
    {
      children: t('moveUp'),
      key: 'moveElementUp',
      disabled: !prevItemId,
      onClick: moveElementUp,
    },
    {
      children: t('moveDown'),
      key: 'moveElementDown',
      disabled: !nextItemId,
      onClick: moveElementDown,
    },
  ];

  return (
    <>
      <ContainerWithCommonContextMenu
        toolTip={t('manageLink', { name: link.name })}
        actions={contextMenuActions}
      >
        <ContentLink
          to={link.url}
          external={link.external}
          newWindowIndicator
          target={link.external ? HyperlinkTarget.BLANK : undefined}
          title={getItemHelpText(getUrlProtocol(link.url), t as TFunction)}
          tooltipPlacement={TooltipPlacement.BOTTOM}
        >
          {link.name || link.url}
        </ContentLink>
      </ContainerWithCommonContextMenu>
      {error && <StatusInfoError message={t('errorResponse')} modifier="padding-top" />}
    </>
  );
}

export const DraggableExternalLinkListItem = asDraggableContentListItem(ExternalLinkListItem);
